import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { format, parse, isValid } from "date-fns";
import toast from 'react-hot-toast';
import api from 'state/axios';
import Papa from 'papaparse';
import { useTheme, useMediaQuery, Box, Typography,  
  CircularProgress, Tooltip, IconButton,
} from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import DeleteConfirm from 'components/DeleteConfirm';
import DataGridComponent from 'components/DataGridComponent';
import NonCrewMemberFilterDialog from './NonCrewMemberFilterDialog';
import NonCrewMemberList from "./NonCrewMemberList";


const NonCrewMembers = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user); 
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "NonCrewMembers", "create", false);
  const canEdit = hasPermission(user?.permissions, "NonCrewMembers", "update", false);
  const canUpsert = hasPermission(user?.permissions, "NonCrewMembers", "upsert", false);
  const canDelete = hasPermission(user?.permissions, "NonCrewMembers", "delete", false);        

  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(() => ({ isActive: 'ACTIVE' }));
  const defaultFilters = {
    isActive: 'ACTIVE',
  };

  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [idToDelete, setIdToDelete] = useState(null);
  const [fileName, setFileName] = useState("");
  const [nonCrewMembers, setNonCrewMembers] = useState([]);
  
  
  const fetchNonCrewMembers = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      
      const url = '/noncrewmembers';      
      const res = await api.get(url);

      const updatedNonCrewMember = res.data?.map(member => {
        const contactTel = member['contactTel'] || member['contactCell'];
        return {
          ...member,
          contactTel,
        };
      });

      setNonCrewMembers(updatedNonCrewMember);
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to fetch non crew members!");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNonCrewMembers();      
  }, [fetchNonCrewMembers]); 

  const handleUpdateNonCrewMembers = useCallback(async () => {    
    try {
      if (!startDate || !endDate) return [];

      setLoading(true);
      const url = '/noncrewmembers/insert';
      const payload = { startDate, endDate };          
      const res = await api.put(url, payload);

      if (res.data.success) {
        toast.success(res.data.message || 'Non crew members updated successfully.');
        await fetchNonCrewMembers();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update non crew members!");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, fetchNonCrewMembers]); 

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      toast.error("No file selected!");
      return;
    }
  
    setFileName(file.name);
    setLoading(true);
  
    try {
      // Read the file and skip first two rows (headers in row 3)
      const text = await file.text();
      const lines = text.split('\n');
      if (lines.length < 3) {
        toast.error("CSV file does not have enough rows for headers in row 3!");
        setFileName("");
        setLoading(false);
        return;
      }
      
      const parseDate = (dateStr) => {
        if (!dateStr) return null;       
        const date = parse(dateStr, "dd/MM/yyyy", new Date());
        if (!isValid(date)) return null;
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      };     

      const csvContent = lines.slice(2).join('\n'); // Start from row 3
      
      // Parse CSV content using PapaParse
      Papa.parse(csvContent, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim().replace(/\s+/g, ''), // Normalize headers
        transform: (value) => value.trim(),
        complete: async (results) => {
          const csvData = results.data;
          
          // Process each CSV row
          const updates = [];
          for (const row of csvData) {
            if (!row.ContactName) continue; // Skip rows with no ContactName
  
            // Find matching nonCrewMember by staffName
            const match = nonCrewMembers.find(
              (member) =>
                row.ContactName && member.staffName?.toLowerCase() === row.ContactName.toLowerCase()
            );
  
            if (match) {
              const update = {
                _id: match._id,
                passportNumber: row.PassportNumber || match.passportNumber,
                passportCountry: row.PassportNationality || match.passportCountry,
                passportExpiryDate: row.PassportExpiry
                  ? parseDate(row.PassportExpiry)
                  : match.passportExpiryDate,
              };
  
              // Validate date
              if (update.passportExpiryDate && isNaN(update.passportExpiryDate.getTime())) {
                console.warn(`Invalid passport expiry date for ${row.ContactName}: ${row.PassportExpiry}`);
                update.passportExpiryDate = match.passportExpiryDate;
              }
  
              updates.push(update);
            }
          }
          
          if (updates.length === 0) {
            toast.error("No matching records found to update.");
            setFileName("");
            setLoading(false);
            return;
          }
           
          try {
            const response = await api.post(
              '/noncrewmembers/bulk-update',
              { updates },
            );
  
            const { modifiedCount, errors } = response.data;

            if (errors.length > 0) {
              toast.error(`Updated ${modifiedCount} of ${updates.length} non-crew member(s). ${errors.length} errors occurred.`);
            } else {
              toast.success(`Updated ${modifiedCount}/ ${updates.length} non-crew member(s) successfully!`);
            }
  
            await fetchNonCrewMembers(); 
          } catch (err) {
            toast.error("Failed to update non-crew members: " + err.message);
          } finally {
            setFileName("");
            setLoading(false);
          }
        },
        error: (err) => {
          toast.error("Failed to parse CSV file: " + err.message);
          setFileName("");
          setLoading(false);
        },
      });
    } catch (error) {
      toast.error(error?.message || "Failed to upload file!");
      setFileName("");
      setLoading(false);
    }
  };

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };

  const filteredNonCrewMembers = useMemo(() => {
    if (!Array.isArray(nonCrewMembers)) {
      return []; 
    }

    if (!filters) return nonCrewMembers; 

    return nonCrewMembers.filter((member) => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value || (Array.isArray(value) && value.length === 0)) return true;
          
        // Handle date filtering
        if (key === 'birthDay') {
          const formattedBirthDay = member[key] ? format(new Date(member[key]), 'yyyy-MM-dd') : null;
          const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
          return formattedBirthDay === formattedFilterDate;
        }
        
        if (key === 'staffCode') {
          return member[key] === value; 
        }

        if (key === 'staffName' || key === 'email') {
          return member[key]?.toLowerCase().includes(value.toLowerCase()); 
        }

        if (key === 'gender') {
          if (value === 'ALL') {
            return true
          } else {
            return member[key] === value;
          };
        }

        if (key === 'isActive') {
          if (value === 'ALL') return true; 
          if (value === 'ACTIVE') return member[key] === true; 
          if (value === 'INACTIVE') return member[key] === false; 
        }

        // For other fields, continue normal comparison
        return member[key] === value;
      });
    });
  }, [nonCrewMembers, filters]);      
  
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, nonCrewMembers);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/noncrewmembers/delete/${idToDelete}`);

        setNonCrewMembers(nonCrewMembers.filter((item) => item._id !== idToDelete));
        toast.success('Non crew member deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, nonCrewMembers);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/noncrewmembers/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setNonCrewMembers(nonCrewMembers.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete!");
    }
  }, [canDelete, deleteType, idToDelete, rowSelectionModel, nonCrewMembers]);

  const columns = useMemo(() => [    
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      width: 60,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      // ...(() => {
      //   return isMobile ? { width: 60 } : { flex: 1 };
      // })(),
    },
    { 
      field: "staffCode", 
      headerName: "Code", 
      width: 80,
      // ...(() => {
      //   return isMobile ? { width: 80 } : { flex: 1 };
      // })(),
    },
    {
      field: "staffName",
      headerName: "StaffName",
      cellClassName: "name-column--cell",
      width: 280,
      // ...(() => {
      //   return isMobile ? { width: 280 } : { flex: 4 };
      // })(),
    },
    {
      field: "shortName",
      headerName: "ShortName",    
      width: 100,
      // ...(() => {
      //   return isMobile ? { width: 80 } : { flex: 1 };
      // })(),
    },
    {
      field: "gender",
      headerName: "Gender",
      width: 100,
      // ...(() => {
      //   return isMobile ? { width: 80 } : { flex: 1.1 };
      // })(),
    },
    {
      field: "birthday",
      headerName: "Birthday",
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
      width: 120,
      // ...(() => {
      //   return isMobile ? { width: 120 } : { flex: 1.5 };
      // })(),
    }, 
    {
      field: "marital",
      headerName: "Marital",
      width: 100,
      // ...(() => {
      //   return isMobile ? { width: 80 } : { flex: 1.2 };
      // })(),
    },    
    // {
    //   field: "address",
    //   headerName: "Address",
    //   ...(() => {
    //     return isMobile ? { width: 80 } : { flex: 1 };
    //   })(),
    // },
    // {
    //   field: "city",
    //   headerName: "City",
    //   ...(() => {
    //     return isMobile ? { width: 80 } : { flex: 1 };
    //   })(),
    // },
    // {
    //   field: "state",
    //   headerName: "State",
    //   ...(() => {
    //     return isMobile ? { width: 80 } : { flex: 1 };
    //   })(),
    // },
    // {
    //   field: "zip",
    //   headerName: "Zip",
    //   ...(() => {
    //     return isMobile ? { width: 80 } : { flex: 1 };
    //   })(),
    // },
    // {
    //   field: "contactCell",
    //   headerName: "ContactCell",
    // },
    // {
    //   field: "contactCell2",
    //   headerName: "ContactCell2",
    // },
    {
      field: "contactTel",
      headerName: "Phone",
      width: 100,
      valueFormatter: (params) => 
        params ? params.replace(/\s+/g, "") : "",
    },
    // {
    //   field: "contactTel2",
    //   headerName: "ContactTel2",
    // },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      // ...(() => {
      //   return isMobile ? { width: 200 } : { flex: 3 };
      // })(),
    },
    {
      field: "nextKinName",
      headerName: "NextKinName",
      width: 250,
      // ...(() => {
      //   return isMobile ? { width: 250 } : { flex: 3 };
      // })(),
    },
    {
      field: "nextKinRelsn",
      headerName: "NextKinRelsn",
      width: 120,
      // ...(() => {
      //   return isMobile ? { width: 120 } : { flex: 2 };
      // })(),
    },
    {
      field: "nextKinContact",
      headerName: "NextKinContact",
      width: 120,
      // ...(() => {
      //   return isMobile ? { width: 120 } : { flex: 2 };
      // })(),
    },
    // {
    //   field: "contactCoMob",
    //   headerName: "ContactCoMob",
    // },
    // {
    //   field: "contactCoMob2",
    //   headerName: "ContactCoMob2",
    // },
    {
      field: "passportName",
      headerName: "PassportName",
      width: 250,
      // ...(() => {
      //   return isMobile ? { width: 250 } : { flex: 3 };
      // })(),
    },
    {
      field: "passportNumber",
      headerName: "PassportNumber",
      width: 120,
      // ...(() => {
      //   return isMobile ? { width: 120 } : { flex: 2 };
      // })(),
    },    
    {
      field: "passportIssueDate",
      headerName: "IssueDate",      
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
      width: 100,
      // ...(() => {
      //   return isMobile ? { width: 120 } : { flex: 2 };
      // })(),
    },
    {
      field: "passportExpiryDate",
      headerName: "ExpiryDate",
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
      width: 120,
      // ...(() => {
      //   return isMobile ? { width: 120 } : { flex: 2 };
      // })(),
    },
    {
      field: "passportCountry",
      headerName: "PassportCountry",
      width: 120,
      // ...(() => {
      //   return isMobile ? { width: 120 } : { flex: 1.5 };
      // })(),
    },
    {
      field: "nationality",
      headerName: "Nationality",
      width: 120,
      // ...(() => {
      //   return isMobile ? { width: 120 } : { flex: 1.2 };
      // })(),
    },
    {
      field: "isActive",
      headerName: "IsActive",
      width: 100,
      // ...(() => {
      //   return isMobile ? { width: 100 } : { flex: 1.3 };
      // })(),
    },    
  ], []);

  const hiddenColumns = useMemo(() => {  
    return {
      _id: false,
      no: false,
      shortName: false,
      gender: false,
      birthday: false,
      marital: false,
      nextKinName: false,
      nextKinRelsn: false,
      nextKinContact: false,
      isActive: false,
    }
  }, []);

  const nonMobileHiddenColumns = useMemo(() => {
    return {
      _id: false,    
      shortName: false,    
      marital: false,
      nextKinName: false,
      nextKinRelsn: false,
      nextKinContact: false,
    }
  }, []);

  const actionColumn = useMemo(() => [
    {
      field: "action",
      headerName: "Action",
      filterable: false,
      sortable: false,
      width: 90,
      renderCell: (params) => {
        return (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center"
            gap={1}
            sx={{ width: '100%', height: '100%' }}
          >           
            <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                sx={{
                  color: theme.palette.primary.dark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: theme.palette.green.deep,
                  },
                }}
                onClick={() => {
                  if (!canEdit) return;
                  navigate(`/noncrewmembers/${params.row._id}`)
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
                     
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                sx={{
                  color: theme.palette.primary.dark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: theme.palette.green.deep,
                  },
                }}
                onClick={() => {
                  if (!canDelete) return;
                  handleDeleteClick(params.row._id);
                }}
              >
                <DeleteForeverIcon />
              </IconButton>                
            </Tooltip>                     
          </Box>
        );
      },
    }
  ], [theme, canEdit, canDelete, navigate]); 
  
  const genderOptions = useMemo(() => [...new Set(nonCrewMembers.map(m => m.gender))].sort(), [nonCrewMembers]);
  const isActiveOptions = useMemo(() => [...new Set(nonCrewMembers.map(m => m.isActive))].sort(), [nonCrewMembers]);
  
  const gridProps = {
    sx: {
      "& .MuiDataGrid-row.isActive-false": {
        backgroundColor: `${theme.palette.primary.light} !important`,  
      },
    },
    
    getRowClassName: ({ row }) => {
      if (row.isActive === false) return 'isActive-false';
      return '';
    },
  };
  
  const columnsToShow = [...columns, ...actionColumn];
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
  }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);
  
  
  return (
    <Box className="main-container-box">                
      <FlexBetween>
        <Header 
          title="Non Crew Members" 
          subtitle={loading ? "Loading..." : `List of ${filteredNonCrewMembers?.length || 0} Members`}
        /> 
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && (
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Upload & update passports" arrow>
                  <span>
                    <label htmlFor={!!fileName ? undefined : "upload-file"}>
                      <StyledButton 
                        component="span"
                        disabled={!!fileName}
                      >
                        <FileUploadIcon />
                      </StyledButton>
                    </label>
                  </span>
                </Tooltip>
                <input
                  id="upload-file"
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={handleUpload}
                /> 
                <Tooltip title="Insert from Jump Seaters" arrow>
                  <span>
                    <StyledButton 
                      onClick={() => {
                        if (!canUpsert) return;
                        handleUpdateNonCrewMembers();
                      }}
                    >
                      <UpdateIcon />
                    </StyledButton>
                  </span>            
                </Tooltip>
              </Box>
            )}

            <Tooltip title="Bulk soft delete" arrow>
              <span>
                <StyledButton 
                  disabled={!isAnyRowSelected(rowSelectionModel, nonCrewMembers)}
                  onClick={() => {
                    if (!canDelete) return;
                    handleBulkDelete();
                  }}
                >
                  <DeleteIcon />
                </StyledButton>
              </span>
            </Tooltip>

            <Tooltip title="Create" arrow>
              <span>
                <StyledButton 
                  onClick={() => {
                    if (!canCreate) return;
                    navigate("/noncrewmembers/new");
                  }}
                >
                  <AddCircleOutlineIcon />
                </StyledButton>
              </span>
            </Tooltip>
            
            <FilterButton
              filters={filters}
              defaultFilters={defaultFilters}
              onClick={handleFilterOpen}
            />    
          </Box>
        )}                
      </FlexBetween>            
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
          sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
          >                            
            {isMobile ? (
              <NonCrewMemberList
                rows={filteredNonCrewMembers}
                onEdit={(member) => navigate(`/noncrewmembers/${member._id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={filteredNonCrewMembers}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                setRowSelectionModel={setRowSelectionModel}
                rowSelectionModel={rowSelectionModel}
                loading={loading}
                props={gridProps}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
                    
          {isFilterDialogOpen &&
            <NonCrewMemberFilterDialog
              open={isFilterDialogOpen}
              onClose={handleFilterClose}
              onApply={handleFilterApply}
              genderOptions={genderOptions}
              isActiveOptions={isActiveOptions}
              currentFilters={filters}
            />
          }
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default NonCrewMembers;
